/**
 * PendingAiUseCasesPanel — sits above the main AI Use Case Register table (AiUseCaseList.tsx).
 * A draft/submitted/approved use case whose 7-item compliance checklist (submission/DPIA/
 * Subprocessor/Processing Activity/Privacy Policy/interested-party notice/Data Inventory) isn't
 * yet fully satisfied shows up here instead of the main register - AiUseCaseList.tsx's own
 * extraFilters entry is what actually excludes it from the table below; this component only
 * renders it. An 'active' record can never actually appear here: it is only ever reached once
 * this exact checklist is already fully satisfied (see the backend's own
 * _refresh_compliance_and_maybe_activate).
 *
 * Redesigned (2026-09-06) around one problem: with several pending use cases, seven full rows
 * each was a wall of near-identical text. Closed by default - a ring (done/needed, not
 * MetricRing's value/target - different semantics, not reused) plus a one-line "what's next"
 * summary. Opening a row shows only what's still outstanding; finished items collapse into one
 * muted line instead of repeating state that hasn't changed. Single-open accordion, so the panel
 * stays compact regardless of how many use cases are pending.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRegisterItems } from '../RegisterItemList';
import { aiUseCaseApi } from '../../services/api';
import { invalidateAiUseCaseLists, writeAiUseCase } from './aiUseCaseKeys';
import { useToast } from '../../hooks/useToast';
import type { AiUseCase, ComplianceChecklistItem } from '../../types/models';

type ChecklistKey = 'submitted' | 'dpia' | 'subprocessor' | 'processingActivity' | 'privacyPolicy' | 'dataInventory';

/** `nextPhrase` is what fills "Just one step left — …" when this is the ONLY outstanding item -
 *  written as a plain verb phrase so it reads naturally after that lead-in. `dataInventory` never
 *  appears there (see the header comment - it's always done the instant it's needed). */
const CHECKLIST_ROW_DEFS: { key: ChecklistKey; label: string; nextPhrase: string }[] = [
  { key: 'submitted', label: 'AI Use Case Submitted', nextPhrase: 'submit it for review' },
  { key: 'dpia', label: 'DPIA', nextPhrase: 'publish the DPIA' },
  { key: 'subprocessor', label: 'Subprocessor / Processor List', nextPhrase: 'activate the linked subprocessor' },
  { key: 'processingActivity', label: 'Processing Activity List', nextPhrase: 'activate the linked processing activity' },
  { key: 'privacyPolicy', label: 'Privacy Policy', nextPhrase: 'review the proposed Privacy Policy change' },
  { key: 'dataInventory', label: 'Data Inventory', nextPhrase: '' },
];

type RowStatus = 'inProgress' | 'notStarted' | 'awaitingApproval';

/** Done items never reach here (the caller already filters to needed && !done) - this only
 *  distinguishes the three flavours of NOT done. Same underlying "nothing drafted yet" state
 *  reads very differently depending on why: "Awaiting approval" (still under review, nothing
 *  could have happened yet) is not a problem; "Retry drafting" (approved, but drafting never
 *  completed) is. The backend's own linkLabel already carries this distinction. */
const statusOf = (item: ComplianceChecklistItem): RowStatus => {
  if (item.linkLabel === 'Awaiting approval') return 'awaitingApproval';
  return item.linkTo.startsWith('/ai-use-cases/') ? 'notStarted' : 'inProgress';
};

const STATUS_META: Record<RowStatus, { tone: string; icon: string }> = {
  inProgress: { tone: 'warning', icon: 'ri-time-line' },
  notStarted: { tone: 'danger', icon: 'ri-refresh-line' },
  awaitingApproval: { tone: 'neutral', icon: 'ri-hourglass-line' },
};

/** A fraction ring (done/needed), not CatalogPage.tsx's MetricRing - that one is shaped around a
 *  value/target metric, a different semantic, and this session's own standing instruction is to
 *  rebuild this section rather than adapt something that doesn't fit. Same SVG stroke-dasharray
 *  technique that component already established for this app, so it still feels native. */
const ChecklistRing: React.FC<{ done: number; total: number; tone: string }> = ({ done, total, tone }) => {
  const size = 40;
  const stroke = 3.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 100;
  const dash = (pct / 100) * circumference;
  return (
    <div className="np-checklist-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} />
        <circle
          className="fill" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke}
          stroke={`var(--bs-${tone})`}
          strokeDasharray={`${dash} ${circumference}`}
        />
      </svg>
      <span className="frac">{done}/{total}</span>
    </div>
  );
};

const OutstandingItem: React.FC<{ label: string; item: ComplianceChecklistItem }> = ({ label, item }) => {
  const status = statusOf(item);
  const meta = STATUS_META[status];
  return (
    <div className="np-checklist-item">
      <div className={`np-checklist-item-dot np-tone-${meta.tone}`}><i className={meta.icon}></i></div>
      <div className="np-checklist-item-body">
        <div className="np-checklist-item-label">{label}</div>
        <div className="np-checklist-item-status">{item.linkLabel}</div>
      </div>
      {status === 'awaitingApproval' ? (
        <span className="np-checklist-item-action muted">Not yet</span>
      ) : (
        // New tab - clicking through to a document/subprocessor/processing activity from here is
        // a "check on this, then come back" action, not a "navigate away from the checklist" one;
        // the panel's own open/closed accordion state would otherwise be lost on return.
        <Link to={item.linkTo} target="_blank" rel="noopener noreferrer" className="np-checklist-item-action">
          {item.linkLabel}
        </Link>
      )}
    </div>
  );
};

/** Only ever rendered for a NOT-YET-sent distribution - once sent, it folds into the done-strip
 *  text instead (see UseCaseRow), same as every other finished item. */
const DistributionItem: React.FC<{
  useCaseId: string;
  privacyPolicyDone: boolean;
}> = ({ useCaseId, privacyPolicyDone }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () => aiUseCaseApi.markPrivacyPolicySent(useCaseId),
    onSuccess: (updated) => {
      writeAiUseCase(queryClient, updated);
      invalidateAiUseCaseLists(queryClient);
      toast.success('Marked as sent.');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to mark as sent.'),
  });

  if (!privacyPolicyDone) {
    return (
      <div className="np-checklist-item">
        <div className="np-checklist-item-dot np-tone-neutral"><i className="ri-hourglass-line"></i></div>
        <div className="np-checklist-item-body">
          <div className="np-checklist-item-label">Privacy Policy sent to interested parties</div>
          <div className="np-checklist-item-status">Privacy Policy update not resolved yet</div>
        </div>
        <span className="np-checklist-item-action muted">Not yet</span>
      </div>
    );
  }
  return (
    <div className="np-checklist-manual">
      <div className="np-checklist-item-dot np-tone-success"><i className="ri-mail-send-line"></i></div>
      <div className="np-checklist-item-body">
        <div className="np-checklist-item-label">Privacy Policy sent to interested parties</div>
        <div className="np-checklist-item-status">Manual step — confirm once notified</div>
      </div>
      <button
        type="button"
        className="np-checklist-manual-btn"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? 'Marking…' : 'Mark as sent'}
      </button>
    </div>
  );
};

const STATUS_CHIP: Record<string, { tone: string; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  submitted: { tone: 'info', label: 'Under review' },
  approved: { tone: 'info', label: 'Approved' },
  active: { tone: 'success', label: 'Active' },
};

const UseCaseRow: React.FC<{ uc: AiUseCase; open: boolean; onToggle: () => void }> = ({ uc, open, onToggle }) => {
  const checklist = uc.complianceChecklist!;
  const items = checklist.items;

  const doneDefs = CHECKLIST_ROW_DEFS.filter((d) => items[d.key].needed && items[d.key].done);
  const outstandingDefs = CHECKLIST_ROW_DEFS.filter((d) => items[d.key].needed && !items[d.key].done);
  const distributionNeeded = items.privacyPolicyDistribution.needed;
  const distributionDone = items.privacyPolicyDistribution.done;

  const totalNeeded = CHECKLIST_ROW_DEFS.filter((d) => items[d.key].needed).length + (distributionNeeded ? 1 : 0);
  const totalDone = doneDefs.length + (distributionNeeded && distributionDone ? 1 : 0);
  const remaining = totalNeeded - totalDone;

  const ringTone = uc.status !== 'approved' ? 'info' : remaining <= 1 ? 'success' : 'warning';

  let nextLine: string;
  if (uc.status === 'draft') {
    nextLine = 'Draft — not yet submitted for review.';
  } else if (uc.status === 'submitted') {
    nextLine = 'Submitted — awaiting the AI review before anything else can start.';
  } else if (remaining === 1) {
    if (outstandingDefs.length === 0 && distributionNeeded && !distributionDone) {
      nextLine = 'Just one step left — send the Privacy Policy update to interested parties.';
    } else {
      nextLine = `Just one step left — ${outstandingDefs[0]?.nextPhrase || 'finish this item'}.`;
    }
  } else {
    nextLine = `${remaining} items still need attention.`;
  }

  const statusChip = STATUS_CHIP[uc.status] || STATUS_CHIP.draft;

  return (
    <div className={`np-checklist-row${open ? ' open' : ''}`}>
      <button type="button" className="np-checklist-row-summary" onClick={onToggle}>
        <ChecklistRing done={totalDone} total={totalNeeded} tone={ringTone} />
        <div className="np-checklist-row-main">
          <div className="np-checklist-row-title-line">
            <Link
              to={`/ai-use-cases/${uc.useCaseId}`}
              className="np-checklist-row-title"
              onClick={(e) => e.stopPropagation()}
            >
              {uc.name || uc.useCaseId}
            </Link>
            <span className={`np-chip np-tone-${statusChip.tone}`}>{statusChip.label}</span>
          </div>
          <div className="np-checklist-row-next">{nextLine}</div>
        </div>
        <div className="np-checklist-chevron"><i className="ri-arrow-down-s-line"></i></div>
      </button>
      <div className="np-checklist-detail">
        <div className="np-checklist-detail-inner">
          <div className="np-checklist-detail-pad">
            {doneDefs.length > 0 && (
              <div className="np-checklist-done-strip">
                <i className="ri-checkbox-circle-fill"></i>
                Done: {doneDefs.map((d) => d.label).join(' · ')}
                {distributionNeeded && distributionDone ? ' · Privacy Policy sent' : ''}
              </div>
            )}
            {outstandingDefs.map((d) => (
              <OutstandingItem key={d.key} label={d.label} item={items[d.key]} />
            ))}
            {distributionNeeded && !distributionDone && (
              <DistributionItem
                useCaseId={uc.useCaseId}
                privacyPolicyDone={items.privacyPolicy.done}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PendingAiUseCasesPanel: React.FC = () => {
  const { data: allUseCases = [] } = useRegisterItems('/ai-use-cases');
  const [openId, setOpenId] = useState<string | null>(null);

  // 'active' is included defensively, not because it should ever actually match: it is only
  // ever reached once the backend's own promotion has already found complete:true, so a
  // record here with status 'active' would mean that invariant broke somewhere - better to
  // still show it (fail open, same reasoning as the register's own filter) than hide it.
  const pending = (allUseCases as AiUseCase[]).filter(
    (u) => (u.status === 'draft' || u.status === 'submitted' || u.status === 'approved' || u.status === 'active')
      && u.complianceChecklist?.complete === false,
  );

  if (pending.length === 0) return null;

  const totalOutstanding = pending.reduce((sum, uc) => {
    const items = uc.complianceChecklist!.items;
    const values: { needed: boolean; done: boolean }[] = Object.values(items);
    return sum + values.filter((i) => i.needed && !i.done).length;
  }, 0);

  return (
    <div className="np-card mb-3">
      <div className="np-checklist-panel-head">
        <div className="np-checklist-panel-icon"><i className="ri-route-line"></i></div>
        <div>
          <h2>Pending AI Use Cases</h2>
          <div className="np-checklist-panel-sub">
            {pending.length} {pending.length === 1 ? 'use case' : 'use cases'} · {totalOutstanding} {totalOutstanding === 1 ? 'item' : 'items'} outstanding across them
          </div>
        </div>
        <span className="np-chip np-tone-warning">{pending.length}</span>
      </div>
      <div className="np-checklist-panel-body">
        {pending.map((uc) => (
          <UseCaseRow
            key={uc.useCaseId}
            uc={uc}
            open={openId === uc.useCaseId}
            onToggle={() => setOpenId((prev) => (prev === uc.useCaseId ? null : uc.useCaseId))}
          />
        ))}
      </div>
    </div>
  );
};

export default PendingAiUseCasesPanel;
